/* Source: /home/sidsolaiyappan/skooch_ai/shared/learned_profile_client.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { get_supabase } from "./supabase_config.js";
const _jac = {
  int: {
    bit_length(n) {
      if (n === 0) return 0;
      return Math.floor(Math.log2(Math.abs(n))) + 1;
    },
    bit_count(n) {
      let x = Math.abs(n);
      let count = 0;
      while (x) { count += x & 1; x >>= 1; }
      return count;
    },
    to_bytes(n, length, byteorder) {
      const bytes = [];
      let val = n < 0 ? (1 << (length * 8)) + n : n;
      for (let i = 0; i < length; i++) {
        bytes.push(val & 0xff);
        val >>= 8;
      }
      if (byteorder === "big") bytes.reverse();
      return bytes;
    },
    from_bytes(bytes, byteorder) {
      const b = byteorder === "big" ? bytes : [...bytes].reverse();
      let result = 0;
      for (const byte of b) result = (result << 8) | byte;
      return result;
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  float: {
    as_integer_ratio(f) {
      if (!isFinite(f)) throw new _jac.exc.ValueError("cannot convert " + f + " to integer ratio");
      if (f === 0) return [0, 1];
      let num = f, den = 1;
      while (num !== Math.floor(num)) { num *= 2; den *= 2; }
      const g = _jac.float._gcd(Math.abs(num), den);
      return [num / g, den / g];
    },
    _gcd(a, b) { while (b) { [a, b] = [b, a % b]; } return a; },
    hex(f) {
      return f.toString(16);
    },
    fromhex(s) {
      return parseFloat(s);
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  complex: {
    conjugate(c) { return {re: c.re, im: -c.im}; },
    add(a, b) { return {re: a.re + b.re, im: a.im + b.im}; },
    sub(a, b) { return {re: a.re - b.re, im: a.im - b.im}; },
    mul(a, b) { return {re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re}; },
    truediv(a, b) {
      const d = b.re * b.re + b.im * b.im;
      return {re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d};
    },
    pow(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      if (b.im === 0 && Number.isInteger(b.re)) {
        let result = {re: 1, im: 0};
        for (let i = 0; i < Math.abs(b.re); i++) result = _jac.complex.mul(result, a);
        return b.re < 0 ? _jac.complex.truediv({re: 1, im: 0}, result) : result;
      }
      const r = Math.sqrt(a.re * a.re + a.im * a.im);
      const theta = Math.atan2(a.im, a.re);
      const lnr = Math.log(r);
      const newR = Math.exp(lnr * b.re - theta * b.im);
      const newTheta = lnr * b.im + theta * b.re;
      return {re: newR * Math.cos(newTheta), im: newR * Math.sin(newTheta)};
    },
    eq(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      return a.re === b.re && a.im === b.im;
    },
    neg(a) { return {re: -a.re, im: -a.im}; },
    pos(a) { return {re: +a.re, im: +a.im}; }
  },

  str: {
    capitalize(s) {
      if (s.length === 0) return s;
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    title(s) {
      return s.replace(/\b\w/g, c => c.toUpperCase());
    },
    swapcase(s) {
      return [...s].map(c => c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()).join("");
    },
    count(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      if (sub === "") return t.length + 1;
      let count = 0, pos = 0;
      while ((pos = t.indexOf(sub, pos)) !== -1) { count++; pos += sub.length; }
      return count;
    },
    find(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.indexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    rfind(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.lastIndexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    index(s, sub, start, end) {
      const idx = _jac.str.find(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(s, sub, start, end) {
      const idx = _jac.str.rfind(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(s, prefix, start, end) {
      return s.slice(start || 0, end).startsWith(prefix);
    },
    endswith(s, suffix, start, end) {
      return s.slice(start || 0, end).endsWith(suffix);
    },
    replace(s, old, new_, count) {
      if (count === undefined) return s.split(old).join(new_);
      let result = s, n = 0;
      while (n < count) {
        const idx = result.indexOf(old);
        if (idx === -1) break;
        result = result.slice(0, idx) + new_ + result.slice(idx + old.length);
        n++;
      }
      return result;
    },
    strip(s, chars) {
      if (!chars) return s.trim();
      let start = 0, end = s.length;
      while (start < end && chars.indexOf(s[start]) !== -1) start++;
      while (end > start && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(start, end);
    },
    lstrip(s, chars) {
      if (!chars) return s.trimStart();
      let start = 0;
      while (start < s.length && chars.indexOf(s[start]) !== -1) start++;
      return s.slice(start);
    },
    rstrip(s, chars) {
      if (!chars) return s.trimEnd();
      let end = s.length;
      while (end > 0 && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(0, end);
    },
    removeprefix(s, prefix) {
      return s.startsWith(prefix) ? s.slice(prefix.length) : s;
    },
    removesuffix(s, suffix) {
      return (suffix && s.endsWith(suffix)) ? s.slice(0, -suffix.length) : s;
    },
    split(s, sep, maxsplit) {
      if (sep === undefined || sep === null) {
        const parts = s.trim().split(/\s+/);
        if (maxsplit === undefined) return parts;
        if (maxsplit <= 0) return [s];
        return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(" ")].filter(x => x !== undefined);
      }
      if (maxsplit === undefined) return s.split(sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(sep)];
    },
    rsplit(s, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.str.split(s, sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      const keep = parts.length - maxsplit;
      return [parts.slice(0, keep).join(sep), ...parts.slice(keep)];
    },
    splitlines(s, keepends) {
      const lines = s.split(/(\r\n|\r|\n)/);
      const result = [];
      for (let i = 0; i < lines.length; i += 2) {
        const line = lines[i];
        const sep = lines[i + 1] || "";
        if (line || sep) result.push(keepends ? line + sep : line);
      }
      return result;
    },
    partition(s, sep) {
      const idx = s.indexOf(sep);
      if (idx === -1) return [s, "", ""];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    rpartition(s, sep) {
      const idx = s.lastIndexOf(sep);
      if (idx === -1) return ["", "", s];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    format(s, ...args) {
      let i = 0;
      return s.replace(/\{(\w*)\}/g, (match, key) => {
        if (key === "") return args[i++];
        if (!isNaN(key)) return args[parseInt(key)];
        return args[0] && typeof args[0] === "object" ? args[0][key] : match;
      });
    },
    format_map(s, mapping) {
      return s.replace(/\{(\w+)\}/g, (match, key) => key in mapping ? mapping[key] : match);
    },
    center(s, width, fillchar) {
      const fc = fillchar || " ";
      if (s.length >= width) return s;
      const total = width - s.length;
      const left = Math.floor(total / 2);
      return fc.repeat(left) + s + fc.repeat(total - left);
    },
    zfill(s, width) {
      if (s.length >= width) return s;
      const sign = (s[0] === "+" || s[0] === "-") ? s[0] : "";
      const body = sign ? s.slice(1) : s;
      return sign + "0".repeat(width - s.length) + body;
    },
    expandtabs(s, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      let result = "", col = 0;
      for (const c of s) {
        if (c === "\t") { const sp = ts - (col % ts); result += " ".repeat(sp); col += sp; }
        else if (c === "\n" || c === "\r") { result += c; col = 0; }
        else { result += c; col++; }
      }
      return result;
    },
    isnumeric(s) {
      if (s.length === 0) return false;
      return [...s].every(c => /[\d\u00B2\u00B3\u00B9\u00BC-\u00BE\u2150-\u218F]/.test(c));
    },
    istitle(s) {
      return s === _jac.str.title(s) && /[A-Za-z]/.test(s);
    },
    translate(s, table) {
      return [...s].map(c => {
        const code = c.charCodeAt(0);
        if (code in table) {
          const v = table[code];
          if (v === null) return "";
          if (typeof v === "number") return String.fromCharCode(v);
          return v;
        }
        return c;
      }).join("");
    },
    maketrans(...args) {
      const table = {};
      if (args.length === 1) {
        const mapping = args[0];
        for (const [k, v] of Object.entries(mapping)) {
          table[typeof k === "string" ? k.charCodeAt(0) : k] = v;
        }
      } else if (args.length >= 2) {
        const from_ = args[0], to_ = args[1];
        for (let i = 0; i < from_.length; i++) {
          table[from_.charCodeAt(i)] = to_.charCodeAt(i);
        }
        if (args[2]) {
          for (const c of args[2]) table[c.charCodeAt(0)] = null;
        }
      }
      return table;
    },
    mod(s, args) {
      let i = 0;
      const a = Array.isArray(args) ? args : [args];
      return s.replace(/%([sd%])/g, (m, fmt) => {
        if (fmt === "%") return "%";
        return String(a[i++]);
      });
    }
  },

  // Runtime-polymorphic method dispatch, used when the codegen cannot prove
  // the static type of a method-call receiver (e.g. a `dict`-typed prop, the
  // result of `or` widening, or a comprehension over an untyped value) and so
  // cannot pick a typed emitter. The compiler emits
  // `_jac.poly.call(obj, "name", ...args)`; `call` resolves the *runtime* type
  // of `obj` and applies the matching Python operation, forwards to a
  // same-named method on a user-defined object, or raises AttributeError. The
  // per-type tables below carry the trivial native-mapped methods inline and
  // delegate the non-trivial ones to the shared `_jac.str/list/dict/set`
  // helpers, so each operation has a single implementation and no Python
  // method name can leak verbatim into the emitted JavaScript.
  poly: {
    _typename(o) {
      if (o === null || o === undefined) return "NoneType";
      if (Array.isArray(o)) return "list";
      if (o instanceof Set) return "set";
      if (o instanceof Map) return "dict";
      if (o instanceof Uint8Array) return "bytes";
      const t = typeof o;
      if (t === "string") return "str";
      if (t === "boolean") return "bool";
      if (t === "number") return Number.isInteger(o) ? "int" : "float";
      if (t === "object") return (o.constructor && o.constructor.name) || "object";
      return t;
    },
    // --- per-runtime-type Python method tables (keyed by Python name) ---
    // Trivial native-mapped ops are inline; non-trivial ones delegate to the
    // shared `_jac.<type>` helpers (which the typed emitters also use), so the
    // typed and polymorphic paths share one implementation.
    _str: {
      lower: (s) => s.toLowerCase(),
      upper: (s) => s.toUpperCase(),
      casefold: (s) => s.toLowerCase(),
      strip: (s, chars) => {
        if (chars === undefined) return s.trim();
        let i = 0, j = s.length;
        while (i < j && chars.indexOf(s[i]) !== -1) i++;
        while (j > i && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(i, j);
      },
      lstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/^\s+/, "");
        let i = 0;
        while (i < s.length && chars.indexOf(s[i]) !== -1) i++;
        return s.slice(i);
      },
      rstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/\s+$/, "");
        let j = s.length;
        while (j > 0 && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(0, j);
      }
    },
    _list: {
      append: (a, x) => { a.push(x); return null; },
      clear: (a) => { a.length = 0; return null; },
      copy: (a) => a.slice(),
      count: (a, x) => _jac.list.count(a, x),
      extend: (a, b) => { for (const v of b) a.push(v); return null; },
      index: (a, x, start, end) => _jac.list.index(a, x, start, end),
      insert: (a, i, x) => { a.splice(i, 0, x); return null; },
      pop: (a, i) => {
        if (i === undefined) return a.pop();
        const idx = i < 0 ? a.length + i : i;
        return a.splice(idx, 1)[0];
      },
      remove: (a, x) => _jac.list.remove(a, x),
      reverse: (a) => { a.reverse(); return null; },
      sort: (a, key) => _jac.list.sort(a, key)
    },
    _dict: {
      clear: (o) => _jac.dict.clear(o),
      copy: (o) => ({ ...o }),
      // Python `dict.get`: returns the stored value when the key is present
      // (even if it is null), and the default only when the key is absent.
      // A membership check is required — `o[key] ?? default_` would wrongly
      // substitute the default for a present-but-null value.
      get: (o, key, default_) =>
        Object.prototype.hasOwnProperty.call(o, key) ? o[key] : default_,
      items: (o) => Object.entries(o),
      keys: (o) => Object.keys(o),
      pop: (o, key, default_) => _jac.dict.pop(o, key, default_),
      popitem: (o) => _jac.dict.popitem(o),
      setdefault: (o, key, default_) => _jac.dict.setdefault(o, key, default_),
      update: (o, other) => {
        if (other instanceof Map) { for (const [k, v] of other) o[k] = v; }
        else if (Array.isArray(other)) { for (const [k, v] of other) o[k] = v; }
        else if (other) { for (const k of Object.keys(other)) o[k] = other[k]; }
        return null;
      },
      values: (o) => Object.values(o)
    },
    _set: {
      add: (s, x) => { s.add(x); return null; },
      clear: (s) => { s.clear(); return null; },
      copy: (s) => new Set(s),
      difference: (a, b) => _jac.set.difference(a, b),
      difference_update: (s, o) => _jac.set.difference_update(s, o),
      discard: (s, x) => { s.delete(x); return null; },
      intersection: (a, b) => _jac.set.intersection(a, b),
      intersection_update: (s, o) => _jac.set.intersection_update(s, o),
      isdisjoint: (a, b) => _jac.set.isDisjointFrom(a, b instanceof Set ? b : new Set(b)),
      issubset: (a, b) => _jac.set.isSubsetOf(a, b instanceof Set ? b : new Set(b)),
      issuperset: (a, b) => _jac.set.isSupersetOf(a, b instanceof Set ? b : new Set(b)),
      pop: (s) => _jac.set.pop(s),
      remove: (s, x) => _jac.set.remove(s, x),
      symmetric_difference: (a, b) => _jac.set.symmetricDifference(a, b),
      symmetric_difference_update: (s, o) => _jac.set.symmetric_difference_update(s, o),
      union: (a, b) => _jac.set.union(a, b),
      update: (s, o) => _jac.set.update(s, o)
    },
    // Jac dicts compile to plain objects, but interop code may hand us a real
    // Map; mirror the dict methods over the Map API for that case.
    _map: {
      clear: (m) => { m.clear(); return null; },
      copy: (m) => new Map(m),
      get: (m, key, default_) => (m.has(key) ? m.get(key) : default_),
      items: (m) => [...m.entries()],
      keys: (m) => [...m.keys()],
      pop: (m, key, default_) => {
        if (m.has(key)) { const v = m.get(key); m.delete(key); return v; }
        if (default_ !== undefined) return default_;
        throw new _jac.exc.KeyError("KeyError: " + key);
      },
      popitem: (m) => {
        if (m.size === 0) throw new _jac.exc.KeyError("dictionary is empty");
        const key = [...m.keys()].pop();
        const val = m.get(key);
        m.delete(key);
        return [key, val];
      },
      setdefault: (m, key, default_) => {
        if (!m.has(key)) m.set(key, default_ === undefined ? null : default_);
        return m.get(key);
      },
      update: (m, other) => {
        if (other instanceof Map) { for (const [k, v] of other) m.set(k, v); }
        else if (Array.isArray(other)) { for (const [k, v] of other) m.set(k, v); }
        else if (other) { for (const k of Object.keys(other)) m.set(k, other[k]); }
        return null;
      },
      values: (m) => [...m.values()]
    },
    // Single entry point. `obj.method(...args)` on a receiver whose static
    // type is not provably primitive is compiled to
    // `_jac.poly.call(obj, "method", ...args)`.
    call(o, m, ...args) {
      // 1. Genuine JS primitives get Python-semantic dispatch: a plain object
      //    is a Python dict, an Array is a list, a Set/Map/string/number/bytes
      //    maps to its Python counterpart. These are the types whose Python
      //    method semantics differ from (or have no) native JS equivalent.
      if (typeof o === "string") {
        if (_jac.poly._str[m]) return _jac.poly._str[m](o, ...args);
        if (_jac.str[m]) return _jac.str[m](o, ...args);
      } else if (typeof o === "number") {
        const ns = Number.isInteger(o) ? _jac.int : _jac.float;
        if (ns && ns[m]) return ns[m](o, ...args);
      } else if (Array.isArray(o)) {
        if (_jac.poly._list[m]) return _jac.poly._list[m](o, ...args);
      } else if (o instanceof Set) {
        if (_jac.poly._set[m]) return _jac.poly._set[m](o, ...args);
      } else if (o instanceof Map) {
        if (_jac.poly._map[m]) return _jac.poly._map[m](o, ...args);
      } else if (o instanceof Uint8Array) {
        if (_jac.bytes[m]) return _jac.bytes[m](o, ...args);
      } else if (
        o !== null && o !== undefined && typeof o === "object"
        && (Object.getPrototypeOf(o) === Object.prototype
            || Object.getPrototypeOf(o) === null)
      ) {
        // A plain object is a Python dict literal.
        if (_jac.poly._dict[m]) return _jac.poly._dict[m](o, ...args);
      }
      // 2. Otherwise forward to a same-named method the receiver carries
      //    itself: a user-defined class instance, a host/framework object
      //    (Headers, URLSearchParams, ...), a callable with attached methods,
      //    or a Set/Map/Array method not mirrored above. This matches the
      //    direct `obj.method(...)` that a typed call would have emitted.
      if (o !== null && o !== undefined && typeof o[m] === "function") {
        return o[m](...args);
      }
      // 3. Nothing applies: raise the Python-style error instead of leaking a
      //    method name or silently returning a wrong value.
      throw new _jac.exc.AttributeError(
        "'" + _jac.poly._typename(o) + "' object has no attribute '" + m + "'");
    },
    // Backward-compatible named shims. The compiler now emits `call`, but a
    // previously-built bundle may still reference a named helper; these route
    // it through the same dispatcher (rest params preserve the arg count).
    lower(s, ...r) { return _jac.poly.call(s, "lower", ...r); },
    upper(s, ...r) { return _jac.poly.call(s, "upper", ...r); },
    strip(s, ...r) { return _jac.poly.call(s, "strip", ...r); },
    lstrip(s, ...r) { return _jac.poly.call(s, "lstrip", ...r); },
    rstrip(s, ...r) { return _jac.poly.call(s, "rstrip", ...r); },
    append(a, ...r) { return _jac.poly.call(a, "append", ...r); },
    extend(a, ...r) { return _jac.poly.call(a, "extend", ...r); },
    pop(a, ...r) { return _jac.poly.call(a, "pop", ...r); },
    get(o, ...r) { return _jac.poly.call(o, "get", ...r); },
    contains(c, x) {
      // Python-equivalent membership for a value of unknown static type.
      // string -> substring; array (list/tuple/range) -> element;
      // Set/Map -> key/element; plain object (dict) -> own key.
      if (typeof c === "string") return c.includes(x);
      if (Array.isArray(c)) return c.includes(x);
      if (c instanceof Set || c instanceof Map) return c.has(x);
      if (c !== null && typeof c === "object") {
        return Object.prototype.hasOwnProperty.call(c, x);
      }
      return false;
    },
    getitem(c, i) {
      // Subscript read for a value of unknown static type. This is a faithful
      // superset of a raw `c[i]`: it only adds Python's negative indexing for
      // sequences (string/array) and otherwise preserves JS semantics exactly
      // (mapping/object access, out-of-range -> `undefined`, no new throws).
      if (typeof i === "number" && i < 0
          && (typeof c === "string" || Array.isArray(c))) {
        return c[c.length + i];
      }
      if (c instanceof Map) return c.get(i);
      return c[i];
    },
    setitem(c, i, v) {
      // Subscript assignment for a value of unknown/list static type. A
      // negative index writes the array element counted from the end (a raw
      // JS `c[-1] = v` would instead create a bogus "-1" property); every
      // other case behaves like a plain `c[i] = v`.
      if (typeof i === "number" && i < 0 && Array.isArray(c)) {
        c[c.length + i] = v;
        return v;
      }
      if (c instanceof Map) { c.set(i, v); return v; }
      c[i] = v;
      return v;
    },
    and_(a, bf) {
      // Python `a and b`: returns `a` when it is falsy, else evaluates and
      // returns `b`. `bf` is a thunk so the right operand keeps short-circuit
      // (lazy) evaluation, and `a` is evaluated exactly once by the caller.
      return _jac.builtin.bool(a) ? bf() : a;
    },
    or_(a, bf) {
      // Python `a or b`: returns `a` when it is truthy, else `b`.
      return _jac.builtin.bool(a) ? a : bf();
    }
  },

  list: {
    remove(arr, val) {
      const i = arr.indexOf(val);
      if (i === -1) throw new _jac.exc.ValueError("list.remove(x): x not in list");
      arr.splice(i, 1);
    },
    index(arr, val, start, end) {
      const s = start || 0;
      const e = end === undefined ? arr.length : end;
      for (let i = s; i < e; i++) {
        if (arr[i] === val) return i;
      }
      throw new _jac.exc.ValueError(val + " is not in list");
    },
    count(arr, val) {
      let c = 0;
      for (const item of arr) { if (item === val) c++; }
      return c;
    },
    sort(arr, key) {
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
    },
    repeat(arr, n) {
      const result = [];
      for (let i = 0; i < n; i++) result.push(...arr);
      return result;
    },
    eq(a, b) { return JSON.stringify(a) === JSON.stringify(b); },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.list.lt(b, a); },
    le(a, b) { return _jac.list.eq(a, b) || _jac.list.lt(a, b); },
    ge(a, b) { return _jac.list.eq(a, b) || _jac.list.gt(a, b); },
    imul(arr, n) {
      const orig = [...arr];
      arr.length = 0;
      for (let i = 0; i < n; i++) arr.push(...orig);
      return arr;
    }
  },

  dict: {
    pop(d, key, default_) {
      if (key in d) { const v = d[key]; delete d[key]; return v; }
      if (default_ !== undefined) return default_;
      throw new _jac.exc.KeyError("KeyError: " + key);
    },
    popitem(d) {
      const keys = Object.keys(d);
      if (keys.length === 0) throw new _jac.exc.KeyError("dictionary is empty");
      const key = keys[keys.length - 1];
      const val = d[key];
      delete d[key];
      return [key, val];
    },
    setdefault(d, key, default_) {
      if (!(key in d)) d[key] = default_ === undefined ? null : default_;
      return d[key];
    },
    clear(d) {
      for (const key of Object.keys(d)) delete d[key];
    },
    fromkeys(keys, value) {
      const d = {};
      const v = value === undefined ? null : value;
      for (const k of keys) d[k] = v;
      return d;
    },
    eq(a, b) {
      const ka = Object.keys(a), kb = Object.keys(b);
      if (ka.length !== kb.length) return false;
      for (const k of ka) {
        if (!(k in b)) return false;
        const va = a[k], vb = b[k];
        if (va !== vb) {
          if (typeof va === "object" && typeof vb === "object" && va !== null && vb !== null) {
            if (JSON.stringify(va) !== JSON.stringify(vb)) return false;
          } else return false;
        }
      }
      return true;
    }
  },

  set: {
    remove(s, val) {
      if (!s.has(val)) throw new _jac.exc.KeyError("KeyError: " + val);
      s.delete(val);
    },
    pop(s) {
      if (s.size === 0) throw new _jac.exc.KeyError("pop from an empty set");
      const val = s.values().next().value;
      s.delete(val);
      return val;
    },
    update(s, other) { for (const v of other) s.add(v); },
    intersection_update(s, other) {
      const otherSet = other instanceof Set ? other : new Set(other);
      for (const v of s) { if (!otherSet.has(v)) s.delete(v); }
    },
    difference_update(s, other) {
      for (const v of other) s.delete(v);
    },
    symmetric_difference_update(s, other) {
      for (const v of other) { if (s.has(v)) s.delete(v); else s.add(v); }
    },
    union(a, b) {
      const r = new Set(a);
      for (const v of b) r.add(v);
      return r;
    },
    intersection(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (bs.has(v)) r.add(v); }
      return r;
    },
    difference(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (!bs.has(v)) r.add(v); }
      return r;
    },
    symmetricDifference(a, b) {
      const r = new Set(a);
      for (const v of b) { if (r.has(v)) r.delete(v); else r.add(v); }
      return r;
    },
    isSubsetOf(a, b) {
      if (a.size > b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    isSupersetOf(a, b) { return _jac.set.isSubsetOf(b, a); },
    isDisjointFrom(a, b) {
      for (const v of a) { if (b.has(v)) return false; }
      return true;
    },
    is_proper_subset(a, b) {
      if (a.size >= b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    is_proper_superset(a, b) { return _jac.set.is_proper_subset(b, a); },
    eq(a, b) {
      if (a.size !== b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    }
  },

  bytes: {
    hex(b) {
      return Array.from(b).map(x => x.toString(16).padStart(2, "0")).join("");
    },
    fromhex(s) {
      const arr = [];
      const clean = s.replace(/\s/g, "");
      for (let i = 0; i < clean.length; i += 2) arr.push(parseInt(clean.substr(i, 2), 16));
      return new Uint8Array(arr);
    },
    _indexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return s;
      for (let i = s; i <= e - sl; i++) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    _lastIndexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return e;
      for (let i = e - sl; i >= s; i--) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    count(b, sub) {
      let c = 0, p = 0;
      while (p <= b.length - sub.length) {
        const idx = _jac.bytes._indexOf(b, sub, p);
        if (idx === -1) break;
        c++; p = idx + sub.length;
      }
      return c;
    },
    find(b, sub, start, end) { return _jac.bytes._indexOf(b, sub, start, end); },
    rfind(b, sub, start, end) { return _jac.bytes._lastIndexOf(b, sub, start, end); },
    index(b, sub, start, end) {
      const idx = _jac.bytes._indexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(b, sub, start, end) {
      const idx = _jac.bytes._lastIndexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(b, prefix, start, end) {
      const s = start || 0;
      if (prefix.length > b.length - s) return false;
      for (let i = 0; i < prefix.length; i++) { if (b[s + i] !== prefix[i]) return false; }
      return true;
    },
    endswith(b, suffix, start, end) {
      const e = end === undefined ? b.length : end;
      if (suffix.length > e) return false;
      const off = e - suffix.length;
      for (let i = 0; i < suffix.length; i++) { if (b[off + i] !== suffix[i]) return false; }
      return true;
    },
    replace(b, old_, new_, count) {
      const r = [];
      let pos = 0, n = 0;
      const max = count === undefined ? Infinity : count;
      while (pos <= b.length) {
        if (n < max) {
          const idx = _jac.bytes._indexOf(b, old_, pos);
          if (idx !== -1) {
            for (let i = pos; i < idx; i++) r.push(b[i]);
            for (let i = 0; i < new_.length; i++) r.push(new_[i]);
            pos = idx + old_.length; n++;
            continue;
          }
        }
        for (let i = pos; i < b.length; i++) r.push(b[i]);
        break;
      }
      return new Uint8Array(r);
    },
    _isWS(c) { return c === 32 || c === 9 || c === 10 || c === 13 || c === 11 || c === 12; },
    strip(b, chars) {
      let s = 0, e = b.length;
      if (chars) { const cs = new Set(chars); while (s < e && cs.has(b[s])) s++; while (e > s && cs.has(b[e-1])) e--; }
      else { while (s < e && _jac.bytes._isWS(b[s])) s++; while (e > s && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(s, e);
    },
    lstrip(b, chars) {
      let s = 0;
      if (chars) { const cs = new Set(chars); while (s < b.length && cs.has(b[s])) s++; }
      else { while (s < b.length && _jac.bytes._isWS(b[s])) s++; }
      return b.slice(s);
    },
    rstrip(b, chars) {
      let e = b.length;
      if (chars) { const cs = new Set(chars); while (e > 0 && cs.has(b[e-1])) e--; }
      else { while (e > 0 && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(0, e);
    },
    removeprefix(b, prefix) {
      if (_jac.bytes.startswith(b, prefix)) return b.slice(prefix.length);
      return b.slice();
    },
    removesuffix(b, suffix) {
      if (suffix.length > 0 && _jac.bytes.endswith(b, suffix)) return b.slice(0, b.length - suffix.length);
      return b.slice();
    },
    split(b, sep, maxsplit) {
      const parts = [];
      let pos = 0, n = 0;
      const max = maxsplit === undefined ? Infinity : maxsplit;
      while (pos <= b.length) {
        if (n >= max) { parts.push(b.slice(pos)); return parts; }
        const idx = _jac.bytes._indexOf(b, sep, pos);
        if (idx === -1) { parts.push(b.slice(pos)); return parts; }
        parts.push(b.slice(pos, idx));
        pos = idx + sep.length; n++;
      }
      if (pos <= b.length) parts.push(b.slice(pos));
      return parts;
    },
    rsplit(b, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.bytes.split(b, sep);
      const parts = [];
      let end = b.length, n = 0;
      while (end >= 0 && n < maxsplit) {
        const idx = _jac.bytes._lastIndexOf(b, sep, 0, end);
        if (idx === -1) break;
        parts.unshift(b.slice(idx + sep.length, end));
        end = idx; n++;
      }
      parts.unshift(b.slice(0, end));
      return parts;
    },
    splitlines(b, keepends) {
      const result = [];
      let start = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 10 || b[i] === 13) {
          let eol = i + 1;
          if (b[i] === 13 && i + 1 < b.length && b[i + 1] === 10) eol++;
          result.push(keepends ? b.slice(start, eol) : b.slice(start, i));
          start = eol;
          if (eol > i + 1) i++;
        }
      }
      if (start < b.length) result.push(b.slice(start));
      return result;
    },
    join(sep, parts) {
      if (parts.length === 0) return new Uint8Array(0);
      const r = [];
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) for (const x of sep) r.push(x);
        for (const x of parts[i]) r.push(x);
      }
      return new Uint8Array(r);
    },
    partition(b, sep) {
      const idx = _jac.bytes._indexOf(b, sep);
      if (idx === -1) return [b.slice(), new Uint8Array(0), new Uint8Array(0)];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    rpartition(b, sep) {
      const idx = _jac.bytes._lastIndexOf(b, sep);
      if (idx === -1) return [new Uint8Array(0), new Uint8Array(0), b.slice()];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    _isUp(c) { return c >= 65 && c <= 90; },
    _isLo(c) { return c >= 97 && c <= 122; },
    _isAl(c) { return (c >= 65 && c <= 90) || (c >= 97 && c <= 122); },
    _isDig(c) { return c >= 48 && c <= 57; },
    capitalize(b) {
      if (b.length === 0) return new Uint8Array(0);
      const r = new Uint8Array(b.length);
      r[0] = _jac.bytes._isLo(b[0]) ? b[0] - 32 : b[0];
      for (let i = 1; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    lower(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    upper(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i];
      return r;
    },
    title(b) {
      const r = new Uint8Array(b.length);
      let prev = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isAl(b[i])) {
          r[i] = prev ? (_jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i]) : (_jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i]);
          prev = true;
        } else { r[i] = b[i]; prev = false; }
      }
      return r;
    },
    swapcase(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) r[i] = b[i] + 32;
        else if (_jac.bytes._isLo(b[i])) r[i] = b[i] - 32;
        else r[i] = b[i];
      }
      return r;
    },
    center(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const total = width - b.length;
      const left = Math.floor(total / 2);
      const r = new Uint8Array(width);
      r.fill(f, 0, left); r.set(b, left); r.fill(f, left + b.length);
      return r;
    },
    ljust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      r.set(b); r.fill(f, b.length);
      return r;
    },
    rjust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      const off = width - b.length;
      r.fill(f, 0, off); r.set(b, off);
      return r;
    },
    zfill(b, width) {
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      let off = 0;
      if (b.length > 0 && (b[0] === 43 || b[0] === 45)) { r[0] = b[0]; off = 1; }
      const pad = width - b.length;
      r.fill(48, off, off + pad);
      for (let i = off; i < b.length; i++) r[pad + i] = b[i];
      return r;
    },
    expandtabs(b, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      const r = [];
      let col = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 9) { const sp = ts - (col % ts); for (let j = 0; j < sp; j++) r.push(32); col += sp; }
        else if (b[i] === 10 || b[i] === 13) { r.push(b[i]); col = 0; }
        else { r.push(b[i]); col++; }
      }
      return new Uint8Array(r);
    },
    translate(b, table, del_) {
      const r = [];
      const ds = del_ ? new Set(del_) : null;
      for (let i = 0; i < b.length; i++) {
        if (ds && ds.has(b[i])) continue;
        r.push(table && table[b[i]] !== undefined ? table[b[i]] : b[i]);
      }
      return new Uint8Array(r);
    },
    maketrans(from_, to_) {
      const t = new Uint8Array(256);
      for (let i = 0; i < 256; i++) t[i] = i;
      for (let i = 0; i < from_.length; i++) t[from_[i]] = to_[i];
      return t;
    },
    isalnum(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i]) && !_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    isalpha(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i])) return false;
      return true;
    },
    isascii(b) {
      for (let i = 0; i < b.length; i++) if (b[i] > 127) return false;
      return true;
    },
    isdigit(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    islower(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) return false;
        if (_jac.bytes._isLo(b[i])) has = true;
      }
      return has;
    },
    isupper(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isLo(b[i])) return false;
        if (_jac.bytes._isUp(b[i])) has = true;
      }
      return has;
    },
    isspace(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isWS(b[i])) return false;
      return true;
    },
    istitle(b) {
      let prev = false, has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) { if (prev) return false; prev = true; has = true; }
        else if (_jac.bytes._isLo(b[i])) { if (!prev) return false; prev = true; has = true; }
        else prev = false;
      }
      return has;
    },
    add(a, b) {
      const r = new Uint8Array(a.length + b.length);
      r.set(a); r.set(b, a.length);
      return r;
    },
    mul(b, n) {
      const r = new Uint8Array(b.length * n);
      for (let i = 0; i < n; i++) r.set(b, i * b.length);
      return r;
    },
    mod(b, args) {
      const s = new TextDecoder().decode(b);
      return new TextEncoder().encode(_jac.str.mod(s, args));
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.bytes.lt(b, a); },
    le(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.lt(a, b); },
    ge(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.gt(a, b); },
    contains(b, sub) { return _jac.bytes._indexOf(b, sub) !== -1; }
  },

  tuple: {
    count(arr, val) { return _jac.list.count(arr, val); },
    index(arr, val, start, end) { return _jac.list.index(arr, val, start, end); },
    repeat(arr, n) { return _jac.list.repeat(arr, n); },
    eq(a, b) { return _jac.list.eq(a, b); },
    lt(a, b) { return _jac.list.lt(a, b); },
    gt(a, b) { return _jac.list.gt(a, b); },
    le(a, b) { return _jac.list.le(a, b); },
    ge(a, b) { return _jac.list.ge(a, b); }
  },

  range: {
    count(r, val) {
      if (typeof r[Symbol.iterator] === "function") {
        let c = 0;
        for (const v of r) { if (v === val) c++; }
        return c;
      }
      return 0;
    },
    index(r, val) {
      let i = 0;
      for (const v of r) { if (v === val) return i; i++; }
      throw new _jac.exc.ValueError(val + " is not in range");
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) { if (a[i] !== b[i]) return false; }
      return true;
    },
    contains(r, val) {
      for (const v of r) { if (v === val) return true; }
      return false;
    }
  },

  builtin: {
    round(n, ndigits) {
      if (ndigits === undefined) return Math.round(n);
      const f = Math.pow(10, ndigits);
      return Math.round(n * f) / f;
    },
    sum(iterable, start) {
      let total = start || 0;
      for (const v of iterable) total += v;
      return total;
    },
    sorted(iterable, key, reverse) {
      const arr = [...iterable];
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
      if (reverse) arr.reverse();
      return arr;
    },
    enumerate(iterable, start) {
      const s = start || 0;
      return [...iterable].map((v, i) => [s + i, v]);
    },
    zip(...iterables) {
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(arrays.map(a => a[i]));
      }
      return result;
    },
    map(fn, ...iterables) {
      if (iterables.length === 1) return [...iterables[0]].map(fn);
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(fn(...arrays.map(a => a[i])));
      }
      return result;
    },
    filter(fn, iterable) {
      return [...iterable].filter(fn || (x => x));
    },
    // Python subclass relation over builtin-type *tags* -- the string names
    // (`"str"`, `"int"`, ...) that `exit_builtin_type` lowers builtin types to.
    // `object` is the universal base and `bool` is a subclass of `int`; `tuple`
    // and `frozenset` share JS representations with `list`/`set`, so fold those
    // aliases to their runtime tag first. One source of truth so isinstance and
    // issubclass can never disagree.
    _tagSubclass(subTag, superTag) {
      const fold = (t) => t === "tuple" ? "list" : t === "frozenset" ? "set" : t;
      const sub = fold(subTag), sup = fold(superTag);
      if (sup === "object") return true;
      if (sub === sup) return true;
      if (sup === "int" && sub === "bool") return true;
      return false;
    },
    isinstance(o, classinfo) {
      // isinstance(x, (A, B, ...)) -- the tuple of types lowers to a JS array;
      // match if `o` is an instance of any member.
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.isinstance(o, c)) return true;
        }
        return false;
      }
      // Builtin types lower to a string tag (their Python type name): compare
      // the value's runtime type against it via the shared subclass relation.
      if (typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(_jac.poly._typename(o), classinfo);
      }
      // User archetype / JS constructor.
      if (typeof classinfo === "function") return o instanceof classinfo;
      return false;
    },
    issubclass(cls, classinfo) {
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.issubclass(cls, c)) return true;
        }
        return false;
      }
      // When either side is a builtin-type tag, compare by the shared subclass
      // relation (covers `object`, `bool` ⊂ `int`, and identity).
      if (typeof cls === "string" || typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(cls, classinfo);
      }
      if (cls === classinfo) return true;
      let proto = cls;
      while (proto) {
        if (proto === classinfo) return true;
        proto = Object.getPrototypeOf(proto);
      }
      return false;
    },
    id(o) {
      if (o === null || o === undefined) return 0;
      if (typeof o === "number") return o;
      return typeof o === "string" ? _jac.builtin._strHash(o) : 0;
    },
    _strHash(s) {
      let h = 0;
      for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h + s.charCodeAt(i)) | 0;
      }
      return h;
    },
    jid(obj) {
      if (obj === null || obj === undefined) {
        throw new _jac.exc.TypeError(
          "jid() called on " + String(obj)
          + " -- the server may have returned an error instead of a node."
          + " Check that your endpoint returned successfully before using jid()."
        );
      }
      if (obj._jac_id === undefined) {
        throw new _jac.exc.TypeError(
          "jid() expected a node or edge with graph identity, but received "
          + (typeof obj === "object" ? JSON.stringify(Object.keys(obj)) : typeof obj)
          + ". Only node and edge archetypes have a jid."
        );
      }
      return obj._jac_id;
    },
    // unsafe_html(x) returns a sentinel object that the JSX flattener
    // recognises and renders as raw HTML via dangerouslySetInnerHTML.
    // Named "unsafe" so the security implication is visible at the use site.
    unsafe_html(html) {
      return { __jacUnsafeHtml: String(html == null ? "" : html) };
    },
    hash(obj) { return _jac.builtin.id(obj); },
    repr(obj) { return JSON.stringify(obj); },
    pow(base, exp, mod) {
      let result = Math.pow(base, exp);
      if (mod !== undefined) result = ((result % mod) + mod) % mod;
      return result;
    },
    next(iter, default_) {
      const result = iter.next();
      if (result.done) {
        if (default_ !== undefined) return default_;
        throw new _jac.exc.StopIteration("StopIteration");
      }
      return result.value;
    },
    vars(o) {
      if (o === undefined) return {};
      const result = {};
      for (const [k, v] of Object.entries(o)) {
        if (typeof v !== "function") result[k] = v;
      }
      return result;
    },
    dir(o) {
      if (o === undefined) return [];
      return Object.getOwnPropertyNames(o).sort();
    },
    open() { throw new _jac.exc.OSError("open() is not supported in client-side JavaScript"); },
    format(value, format_spec) {
      if (!format_spec) return String(value);
      return String(value);
    },
    ascii(obj) {
      if (typeof obj === "string") {
        let r = "'";
        for (const c of obj) {
          const code = c.charCodeAt(0);
          if (c === "\\") r += "\\\\";
          else if (c === "'") r += "\\'";
          else if (code >= 0x20 && code <= 0x7e) r += c;
          else if (code < 0x100) r += "\\x" + code.toString(16).padStart(2, "0");
          else r += "\\u" + code.toString(16).padStart(4, "0");
        }
        return r + "'";
      }
      return String(obj);
    },
    complex(re, im) { return {re: re || 0, im: im || 0}; },
    bool(x) {
      if (x === null || x === undefined || x === false || x === 0 || x === "" || Number.isNaN(x)) return false;
      if (Array.isArray(x)) return x.length > 0;
      if (x instanceof Set || x instanceof Map) return x.size > 0;
      if (typeof x === "object") return Object.keys(x).length > 0;
      return true;
    },
    range(...args) {
      let start, stop, step;
      if (args.length === 1) { start = 0; stop = args[0]; step = 1; }
      else if (args.length === 2) { start = args[0]; stop = args[1]; step = 1; }
      else { start = args[0]; stop = args[1]; step = args[2]; }
      if (step === 0) throw new _jac.exc.ValueError("range() arg 3 must not be zero");
      const result = [];
      if (step > 0) { for (let i = start; i < stop; i += step) result.push(i); }
      else { for (let i = start; i > stop; i += step) result.push(i); }
      return result;
    },
    slice(...args) {
      if (args.length === 1) return { start: null, stop: args[0], step: null };
      return { start: args[0] !== undefined ? args[0] : null, stop: args[1] !== undefined ? args[1] : null, step: args[2] !== undefined ? args[2] : null };
    },
    format(value, format_spec) {
      if (!format_spec || format_spec === "") return String(value);
      const m = format_spec.match(/^([<>^]?)(\d+)?(?:\.(\d+))?([dfseboxXn%]?)$/);
      if (!m) return String(value);
      const [, align, widthStr, precStr, ftype] = m;
      let s;
      const prec = precStr !== undefined ? parseInt(precStr) : undefined;
      if (ftype === "f" || ftype === "") {
        s = prec !== undefined ? Number(value).toFixed(prec) : String(value);
      } else if (ftype === "d") {
        s = String(Math.floor(Number(value)));
      } else if (ftype === "b") {
        s = Number(value).toString(2);
      } else if (ftype === "o") {
        s = Number(value).toString(8);
      } else if (ftype === "x") {
        s = Number(value).toString(16);
      } else if (ftype === "X") {
        s = Number(value).toString(16).toUpperCase();
      } else if (ftype === "e") {
        s = prec !== undefined ? Number(value).toExponential(prec) : Number(value).toExponential();
      } else if (ftype === "%") {
        s = (prec !== undefined ? (Number(value) * 100).toFixed(prec) : (Number(value) * 100).toFixed(6)) + "%";
      } else if (ftype === "s" || ftype === "n") {
        s = String(value);
      } else {
        s = String(value);
      }
      if (widthStr) {
        const width = parseInt(widthStr);
        if (s.length < width) {
          const pad = " ".repeat(width - s.length);
          if (align === "<") s = s + pad;
          else if (align === "^") {
            const left = Math.floor((width - s.length) / 2);
            s = " ".repeat(left) + s + " ".repeat(width - s.length - left);
          }
          else s = pad + s;
        }
      }
      return s;
    }
  },

  exc: (() => {
    class BaseException extends Error { constructor(m) { super(m); this.name = 'BaseException'; } }
    class Exception extends BaseException { constructor(m) { super(m); this.name = 'Exception'; } }
    class ArithmeticError extends Exception { constructor(m) { super(m); this.name = 'ArithmeticError'; } }
    class ZeroDivisionError extends ArithmeticError { constructor(m) { super(m); this.name = 'ZeroDivisionError'; } }
    class OverflowError extends ArithmeticError { constructor(m) { super(m); this.name = 'OverflowError'; } }
    class FloatingPointError extends ArithmeticError { constructor(m) { super(m); this.name = 'FloatingPointError'; } }
    class LookupError extends Exception { constructor(m) { super(m); this.name = 'LookupError'; } }
    class IndexError extends LookupError { constructor(m) { super(m); this.name = 'IndexError'; } }
    class KeyError extends LookupError { constructor(m) { super(m); this.name = 'KeyError'; } }
    class ValueError extends Exception { constructor(m) { super(m); this.name = 'ValueError'; } }
    class TypeError_ extends Exception { constructor(m) { super(m); this.name = 'TypeError'; } }
    class AttributeError extends Exception { constructor(m) { super(m); this.name = 'AttributeError'; } }
    class RuntimeError extends Exception { constructor(m) { super(m); this.name = 'RuntimeError'; } }
    class NotImplementedError extends RuntimeError { constructor(m) { super(m); this.name = 'NotImplementedError'; } }
    class RecursionError extends RuntimeError { constructor(m) { super(m); this.name = 'RecursionError'; } }
    class OSError extends Exception { constructor(m) { super(m); this.name = 'OSError'; } }
    class FileNotFoundError extends OSError { constructor(m) { super(m); this.name = 'FileNotFoundError'; } }
    class FileExistsError extends OSError { constructor(m) { super(m); this.name = 'FileExistsError'; } }
    class PermissionError extends OSError { constructor(m) { super(m); this.name = 'PermissionError'; } }
    class TimeoutError extends OSError { constructor(m) { super(m); this.name = 'TimeoutError'; } }
    class IsADirectoryError extends OSError { constructor(m) { super(m); this.name = 'IsADirectoryError'; } }
    class NotADirectoryError extends OSError { constructor(m) { super(m); this.name = 'NotADirectoryError'; } }
    class AssertionError extends Exception { constructor(m) { super(m); this.name = 'AssertionError'; } }
    class ImportError extends Exception { constructor(m) { super(m); this.name = 'ImportError'; } }
    class ModuleNotFoundError extends ImportError { constructor(m) { super(m); this.name = 'ModuleNotFoundError'; } }
    class NameError extends Exception { constructor(m) { super(m); this.name = 'NameError'; } }
    class UnboundLocalError extends NameError { constructor(m) { super(m); this.name = 'UnboundLocalError'; } }
    class StopIteration extends Exception { constructor(m) { super(m); this.name = 'StopIteration'; } }
    class StopAsyncIteration extends Exception { constructor(m) { super(m); this.name = 'StopAsyncIteration'; } }
    class EOFError extends Exception { constructor(m) { super(m); this.name = 'EOFError'; } }
    class MemoryError extends Exception { constructor(m) { super(m); this.name = 'MemoryError'; } }
    class ReferenceError_ extends Exception { constructor(m) { super(m); this.name = 'ReferenceError'; } }
    class KeyboardInterrupt extends BaseException { constructor(m) { super(m); this.name = 'KeyboardInterrupt'; } }
    class SystemExit extends BaseException { constructor(m) { super(m); this.name = 'SystemExit'; } }
    class GeneratorExit extends BaseException { constructor(m) { super(m); this.name = 'GeneratorExit'; } }
    return { BaseException, Exception, ArithmeticError, ZeroDivisionError, OverflowError, FloatingPointError, LookupError, IndexError, KeyError, ValueError, TypeError: TypeError_, AttributeError, RuntimeError, NotImplementedError, RecursionError, OSError, FileNotFoundError, FileExistsError, PermissionError, TimeoutError, IsADirectoryError, NotADirectoryError, AssertionError, ImportError, ModuleNotFoundError, NameError, UnboundLocalError, StopIteration, StopAsyncIteration, EOFError, MemoryError, ReferenceError: ReferenceError_, KeyboardInterrupt, SystemExit, GeneratorExit };
  })()
};
function _FP_ORDER_C() {
  return ["morning", "midday", "afternoon", "evening"];
}
function _ATTR_QTYPES_C() {
  return ["move_attribution", "reject_attribution"];
}
function _NONVOTING_QTYPES_C() {
  return ["move_attribution", "reject_attribution", "streak_confirm"];
}
function _CONSENT_SOURCES_C() {
  return ["toast_streak_confirm", "gate_consent"];
}
function _GATE_OPEN_N_C() {
  return 3.0;
}
function _GATE_CLOSE_N_C() {
  return 2.0;
}
function _CONF_HALF_C() {
  return 5.0;
}
function _FAM_SAT_C() {
  return 8.0;
}
function _UNATTRIBUTED_MOVE_MOD_C() {
  return 0.5;
}
function _F2_MERGE_NOISE_FLOOR_C() {
  return 0.35;
}
function _DURATION_CALIB_MULT_C() {
  return {"way_less": 75.0, "about_right": 100.0, "way_more": 135.0};
}
function _DUR_GATE_N_C() {
  return 3.0;
}
function _DUR_CLAMP_LO_C() {
  return 65.0;
}
function _DUR_CLAMP_HI_C() {
  return 135.0;
}
function _AFF_GATE_N_C() {
  return 3;
}
function _AFF_SEP_MARGIN_C() {
  return 0.15;
}
function _AFF_ACCEPT_WEAK_C() {
  return 0.3;
}
function _AFF_TASK_AFFINITY_MULT_C() {
  return 2.0;
}
function _MOVE_DISAVOWALS_C() {
  return ["one_off", "time_blocked"];
}
function _DAY_GATE_N_C() {
  return 3;
}
function _DAY_REJECT_WRONG_C() {
  return (-1.0);
}
function _DAY_MOVE_TO_C() {
  return 0.5;
}
function _DAY_MOVE_FROM_C() {
  return (-0.5);
}
function _parseIsoMs(iso) {
  return Date.parse(iso);
}
function _ageDays(nowMs, occMs) {
  let _d = ((nowMs - occMs) / 86400000.0);
  if ((_d < 0.0)) {
    _d = 0.0;
  }
  return _d;
}
function _decayFactor(ageDays, halfLifeDays) {
  if ((halfLifeDays <= 0.0)) {
    return 1.0;
  }
  return Math.pow(0.5, (ageDays / halfLifeDays));
}
function _pyRound(x) {
  let _f = Math.floor(x);
  let _diff = (x - _f);
  if ((_diff < 0.5)) {
    return _f;
  }
  if ((_diff > 0.5)) {
    return Math.ceil(x);
  }
  if ((_jac.float.mod(_f, 2) === 0)) {
    return _f;
  }
  return (_f + 1);
}
function _focusPeakBucket(slotMinutes) {
  if (((slotMinutes === null) || (slotMinutes === undefined))) {
    return null;
  }
  let _m = Math.trunc(slotMinutes);
  if ((_m < 720)) {
    return "morning";
  }
  if ((_m < 840)) {
    return "midday";
  }
  if ((_m < 1020)) {
    return "afternoon";
  }
  return "evening";
}
function _softBucketWeights(slotMinutes) {
  if (((slotMinutes === null) || (slotMinutes === undefined))) {
    return {};
  }
  let _m = parseFloat(slotMinutes);
  let _boundaries = [720.0, 840.0, 1020.0];
  let _i = 0;
  while ((_i < _boundaries.length)) {
    let _b = _boundaries.at(_i);
    if (((_m > (_b - 60.0)) && (_m < (_b + 60.0)))) {
      let _d = ((_m - (_b - 60.0)) / 120.0);
      let _lo = _jac.poly.getitem(_FP_ORDER_C(), _i);
      let _hi = _jac.poly.getitem(_FP_ORDER_C(), (_i + 1));
      let _res = {};
      _res[_lo] = (1.0 - _d);
      _res[_hi] = _d;
      return _res;
    }
    _i = (_i + 1);
  }
  let _hb = _focusPeakBucket(Math.trunc(_m));
  let _hr = {};
  _hr[_hb] = 1.0;
  return _hr;
}
function _bucketShift(bucket, direction) {
  if (!_FP_ORDER_C().includes(bucket)) {
    return bucket;
  }
  let _idx = _FP_ORDER_C().indexOf(bucket);
  if ((direction === "later")) {
    _idx = (_idx + 1);
  } else if ((direction === "earlier")) {
    _idx = (_idx - 1);
  }
  if ((_idx < 0)) {
    _idx = 0;
  }
  if ((_idx > (_FP_ORDER_C().length - 1))) {
    _idx = (_FP_ORDER_C().length - 1);
  }
  return _jac.poly.getitem(_FP_ORDER_C(), _idx);
}
function _clampOneStep(prev, target) {
  if ((!prev || (prev === target))) {
    return target;
  }
  if ((!_FP_ORDER_C().includes(prev) || !_FP_ORDER_C().includes(target))) {
    return target;
  }
  let _pi = _FP_ORDER_C().indexOf(prev);
  let _ti = _FP_ORDER_C().indexOf(target);
  if ((_ti > _pi)) {
    return _jac.poly.getitem(_FP_ORDER_C(), (_pi + 1));
  }
  if ((_ti < _pi)) {
    return _jac.poly.getitem(_FP_ORDER_C(), (_pi - 1));
  }
  return target;
}
function _attributionModifier(answer) {
  if ((answer === "confirm_peak")) {
    return 4.0;
  }
  if ((answer === "wrong_time")) {
    return 1.0;
  }
  return 0.0;
}
function _attributionIndex(events) {
  if (!_jac.builtin.bool(events)) {
    events = [];
  }
  let _idx = {};
  let _i = 0;
  while ((_i < events.length)) {
    let _e = events.at(_i);
    _i = (_i + 1);
    if (((_e.event_type || "") !== "feedback_answer")) {
      continue;
    }
    let _payload = (_e.payload ? _e.payload : {});
    let _qt = (_payload.question_type || "");
    if (!_ATTR_QTYPES_C().includes(_qt)) {
      continue;
    }
    let _answer = (_payload.answer || "");
    if ((_answer === "_dismissed")) {
      continue;
    }
    let _key = ((String(_payload.target_entity_id) + "|") + String(_payload.target_slot));
    if ((_key in _idx)) {
      continue;
    }
    _idx[_key] = {"answer": _answer, "modifier": _attributionModifier(_answer)};
  }
  return _idx;
}
function _eventAttrSlot(event) {
  let _etype = (event.event_type || "");
  let _payload = (event.payload ? event.payload : {});
  let _slot = event.slot_minutes;
  if (((_slot !== null) && (_slot !== undefined))) {
    return _slot;
  }
  if ((_etype === "placement_moved")) {
    return ((_payload.to_slot !== undefined) ? _payload.to_slot : null);
  }
  if ((_etype === "placement_rejected")) {
    return ((_payload.rejected_slot !== undefined) ? _payload.rejected_slot : null);
  }
  if ((_etype === "placement_accepted")) {
    return ((_payload.slot !== undefined) ? _payload.slot : null);
  }
  return null;
}
function _eventFocusSignal(event) {
  let _etype = (event.event_type || "");
  let _payload = (event.payload ? event.payload : {});
  let _slot = event.slot_minutes;
  if ((_etype === "feedback_answer")) {
    if (((_payload.answer || "") === "_dismissed")) {
      return {"bucket": null};
    }
    if (_NONVOTING_QTYPES_C().includes((_payload.question_type || ""))) {
      return {"bucket": null};
    }
    if (((_payload.dimension || "") !== "focus_peak")) {
      return {"bucket": null};
    }
    let _answer = (_payload.answer || "");
    if (_FP_ORDER_C().includes(_answer)) {
      return {"bucket": _answer, "neg_bucket": null, "sign": 1.0, "secondary_factor": 0.0};
    }
    if ((((_answer === "this is fine") || (_answer === "about right")) || (_answer === "fine"))) {
      let _tslot = ((_payload.target_slot !== undefined) ? _payload.target_slot : null);
      if ((_tslot === null)) {
        return {"bucket": null};
      }
      let _b = _focusPeakBucket(_tslot);
      if (!_b) {
        return {"bucket": null};
      }
      return {"bucket": _b, "neg_bucket": null, "sign": 1.0, "secondary_factor": 0.0, "slot": _tslot};
    }
    let _direction = (_payload.direction || "");
    let _tslot2 = ((_payload.target_slot !== undefined) ? _payload.target_slot : null);
    if ((((_direction === "earlier") || (_direction === "later")) && (_tslot2 !== null))) {
      let _base = _focusPeakBucket(_tslot2);
      if (!_base) {
        return {"bucket": null};
      }
      return {"bucket": _bucketShift(_base, _direction), "neg_bucket": null, "sign": 1.0, "secondary_factor": 0.0};
    }
    return {"bucket": null};
  }
  if ((_etype === "preference_set")) {
    if (((_payload.field || "") !== "focus_peak")) {
      return {"bucket": null};
    }
    let _value = (_payload.value || "");
    if (_FP_ORDER_C().includes(_value)) {
      return {"bucket": _value, "neg_bucket": null, "sign": 1.0, "secondary_factor": 0.0};
    }
    return {"bucket": null};
  }
  if ((_etype === "placement_moved")) {
    let _toSlot = _slot;
    if (((_toSlot === null) || (_toSlot === undefined))) {
      _toSlot = ((_payload.to_slot !== undefined) ? _payload.to_slot : null);
    }
    if ((_toSlot === null)) {
      return {"bucket": null};
    }
    let _toB = _focusPeakBucket(_toSlot);
    if (!_toB) {
      return {"bucket": null};
    }
    let _fromSlotVal = ((_payload.from_slot !== undefined) ? _payload.from_slot : null);
    let _fromB = _focusPeakBucket(_fromSlotVal);
    return {"bucket": _toB, "neg_bucket": _fromB, "sign": 1.0, "secondary_factor": 0.5, "slot": _toSlot, "neg_slot": _fromSlotVal};
  }
  if ((_etype === "placement_rejected")) {
    let _rslot = _slot;
    if (((_rslot === null) || (_rslot === undefined))) {
      _rslot = ((_payload.rejected_slot !== undefined) ? _payload.rejected_slot : null);
    }
    if ((_rslot === null)) {
      return {"bucket": null};
    }
    let _rb = _focusPeakBucket(_rslot);
    if (!_rb) {
      return {"bucket": null};
    }
    return {"bucket": _rb, "neg_bucket": null, "sign": (-1.0), "secondary_factor": 0.0, "slot": _rslot};
  }
  if ((_etype === "placement_accepted")) {
    let _aslot = _slot;
    if (((_aslot === null) || (_aslot === undefined))) {
      _aslot = ((_payload.slot !== undefined) ? _payload.slot : null);
    }
    if ((_aslot === null)) {
      return {"bucket": null};
    }
    let _ab = _focusPeakBucket(_aslot);
    if (!_ab) {
      return {"bucket": null};
    }
    return {"bucket": _ab, "neg_bucket": null, "sign": 1.0, "secondary_factor": 0.0, "weak": 0.3, "slot": _aslot};
  }
  return {"bucket": null};
}
function _eventLabel(etype, sig, payload, modifierSource) {
  let _bucket = null;
  if (sig) {
    _bucket = ((sig.bucket !== undefined) ? sig.bucket : null);
  }
  if ((etype === "placement_moved")) {
    let _base = ("Moved a task to the " + String(_bucket));
    if (modifierSource) {
      _base = (((_base + " (answered: ") + String(modifierSource)) + ")");
    }
    return _base;
  }
  if ((etype === "placement_rejected")) {
    let _base2 = (("Rejected a " + String(_bucket)) + " placement");
    if (modifierSource) {
      _base2 = (((_base2 + " (answered: ") + String(modifierSource)) + ")");
    }
    return _base2;
  }
  if ((etype === "placement_accepted")) {
    return (("Kept a " + String(_bucket)) + " placement");
  }
  if ((etype === "preference_set")) {
    return ("Set focus peak to " + String(_bucket));
  }
  if ((etype === "feedback_answer")) {
    if (_bucket) {
      return ("Told Skooch: " + String(_bucket));
    }
    if (((payload.answer || "") === "_dismissed")) {
      return "Dismissed a question";
    }
    let _qt = (payload.question_type || "");
    if (_ATTR_QTYPES_C().includes(_qt)) {
      return ("Attribution answer: " + String((payload.answer || "")));
    }
    if ((_qt === "streak_confirm")) {
      return ("Consent answer: " + String((payload.answer || "")));
    }
    return "Feedback answer (no focus signal)";
  }
  if ((etype === "task_completed")) {
    return "Completed a task";
  }
  return String(etype);
}
function _isAffirmation(event, movedIds, nowMs, windowDays) {
  if (((event.event_type || "") !== "task_completed")) {
    return false;
  }
  let _payload = (event.payload ? event.payload : {});
  let _sslot = ((_payload.scheduled_slot !== undefined) ? _payload.scheduled_slot : null);
  if ((_sslot === null)) {
    return false;
  }
  let _eid = String(event.entity_id);
  if ((_eid in movedIds)) {
    return false;
  }
  let _occMs = _parseIsoMs(event.occurred_at);
  let _age = _ageDays(nowMs, _occMs);
  if ((_age < 0.0)) {
    _age = 0.0;
  }
  return (_age <= windowDays);
}
function _recentMovedIds(events, nowMs, windowDays) {
  if (!_jac.builtin.bool(events)) {
    events = [];
  }
  let _ids = {};
  let _i = 0;
  while ((_i < events.length)) {
    let _e = events.at(_i);
    _i = (_i + 1);
    if (((_e.event_type || "") !== "placement_moved")) {
      continue;
    }
    let _eid = _e.entity_id;
    if (((_eid === null) || (_eid === undefined))) {
      continue;
    }
    let _occMs = _parseIsoMs(_e.occurred_at);
    let _age = _ageDays(nowMs, _occMs);
    if ((_age <= windowDays)) {
      _ids[String(_eid)] = true;
    }
  }
  return _ids;
}
function _weighEvent(event, attrIndex, nowMs, halfLifeDays) {
  if (!_jac.builtin.bool(attrIndex)) {
    attrIndex = {};
  }
  let _etype = (event.event_type || "");
  let _payload = (event.payload ? event.payload : {});
  let _label = "";
  let _bucket = null;
  let _negBucket = null;
  let _sign = 0.0;
  let _baseWeight = parseFloat((((event.signal_weight !== undefined) && (event.signal_weight !== null)) ? event.signal_weight : 1));
  if (!_baseWeight) {
    _baseWeight = 1.0;
  }
  let _decayV = 0.0;
  let _ageDaysV = 0.0;
  let _modifier = 1.0;
  let _modifierSource = null;
  let _weak = 1.0;
  let _delta = {};
  let _nContrib = 0.0;
  let _excluded = false;
  let _excludedReason = null;
  let _sig = _eventFocusSignal(event);
  if (((_sig.bucket === null) || (_sig.bucket === undefined))) {
    _excluded = true;
    _label = _eventLabel(_etype, _sig, _payload, null);
    if (((_etype === "feedback_answer") && ((_payload.answer || "") === "_dismissed"))) {
      _excludedReason = "dismissed \u2014 counts only toward ask-fatigue backoff";
    } else if ((_etype === "task_completed")) {
      _excludedReason = "confounded: slot was the optimizer's own choice; completion echoes it";
    } else if (((_etype === "feedback_answer") && _NONVOTING_QTYPES_C().includes((_payload.question_type || "")))) {
      _excludedReason = "attribution answer: modifies its target event instead of voting";
    } else {
      _excludedReason = "no time signal";
    }
    return {"event_type": _etype, "occurred_at": (event.occurred_at || ""), "label": _label, "bucket": null, "neg_bucket": null, "sign": _sign, "base_weight": _baseWeight, "decay": _decayV, "age_days": _ageDaysV, "modifier": _modifier, "modifier_source": _modifierSource, "weak": _weak, "delta": _delta, "n_contrib": _nContrib, "excluded": _excluded, "excluded_reason": _excludedReason};
  }
  let _occMs = _parseIsoMs(event.occurred_at);
  _ageDaysV = _ageDays(nowMs, _occMs);
  _decayV = _decayFactor(_ageDaysV, halfLifeDays);
  let _weakV = (((_sig.weak !== undefined) && (_sig.weak !== null)) ? _sig.weak : 1.0);
  let _signV = _sig.sign;
  let _modifierV = 1.0;
  let _modifierSourceV = null;
  if ((((_etype === "placement_moved") || (_etype === "placement_rejected")) || (_etype === "placement_accepted"))) {
    let _attrKey = ((String(event.entity_id) + "|") + String(_eventAttrSlot(event)));
    if ((_attrKey in attrIndex)) {
      _modifierV = attrIndex[_attrKey].modifier;
      _modifierSourceV = attrIndex[_attrKey].answer;
    } else if ((_etype === "placement_moved")) {
      _modifierV = _UNATTRIBUTED_MOVE_MOD_C();
    }
  }
  let _w = (_baseWeight * _decayV);
  let _deltaV = {};
  let _posW = {};
  _posW[_sig.bucket] = 1.0;
  if (((_sig.slot !== undefined) && (_sig.slot !== null))) {
    _posW = _softBucketWeights(_sig.slot);
  }
  let _posKeys = _jac.poly.call(Object, "keys", _posW);
  let _ki = 0;
  while ((_ki < _posKeys.length)) {
    let _k = _posKeys.at(_ki);
    _ki = (_ki + 1);
    let _prev_val = ((_k in _deltaV) ? _deltaV[_k] : 0.0);
    _deltaV[_k] = (_prev_val + ((((_signV * _w) * _weakV) * _modifierV) * _posW[_k]));
  }
  let _negB = ((_sig.neg_bucket !== undefined) ? _sig.neg_bucket : null);
  if (_negB) {
    let _negW = {};
    _negW[_negB] = 1.0;
    if (((_sig.neg_slot !== undefined) && (_sig.neg_slot !== null))) {
      _negW = _softBucketWeights(_sig.neg_slot);
    }
    let _negKeys = _jac.poly.call(Object, "keys", _negW);
    let _nki = 0;
    while ((_nki < _negKeys.length)) {
      let _nk = _negKeys.at(_nki);
      _nki = (_nki + 1);
      let _prev_neg = ((_nk in _deltaV) ? _deltaV[_nk] : 0.0);
      let _secFactor = ((_sig.secondary_factor !== undefined) ? _sig.secondary_factor : 0.5);
      _deltaV[_nk] = (_prev_neg - (((_w * _secFactor) * _modifierV) * _negW[_nk]));
    }
  }
  _label = _eventLabel(_etype, _sig, _payload, _modifierSourceV);
  let _nContribV = _decayV;
  if ((_modifierV <= 0.0)) {
    _nContribV = 0.0;
  }
  return {"event_type": _etype, "occurred_at": (event.occurred_at || ""), "label": _label, "bucket": _sig.bucket, "neg_bucket": _negB, "sign": _signV, "base_weight": _baseWeight, "decay": _decayV, "age_days": _ageDaysV, "modifier": _modifierV, "modifier_source": _modifierSourceV, "weak": _weakV, "delta": _deltaV, "n_contrib": _nContribV, "excluded": false, "excluded_reason": null};
}
function _countFeedbackAnswers(events) {
  if (!_jac.builtin.bool(events)) {
    events = [];
  }
  let _n = 0;
  let _i = 0;
  while ((_i < events.length)) {
    let _e = events.at(_i);
    _i = (_i + 1);
    if (((_e.event_type || "") !== "feedback_answer")) {
      continue;
    }
    let _p = (_e.payload ? _e.payload : {});
    if (((_p.answer || "") === "_dismissed")) {
      continue;
    }
    _n = (_n + 1);
  }
  return _n;
}
function _consentAppliedBucket(events) {
  if (!_jac.builtin.bool(events)) {
    events = [];
  }
  let _i = 0;
  while ((_i < events.length)) {
    let _e = events.at(_i);
    _i = (_i + 1);
    if (((_e.event_type || "") !== "preference_set")) {
      continue;
    }
    let _payload = (_e.payload ? _e.payload : {});
    if (((_payload.field || "") !== "focus_peak")) {
      continue;
    }
    if (!_CONSENT_SOURCES_C().includes((_payload.source || ""))) {
      continue;
    }
    let _value = (_payload.value || "");
    if (_FP_ORDER_C().includes(_value)) {
      return _value;
    }
  }
  return null;
}
function _computeFamiliarity(focusPeakConf, nEff, famSat) {
  if (((famSat === null) || (famSat === undefined))) {
    famSat = 8.0;
  }
  let _eng = Math.min(1.0, (nEff / famSat));
  let _fam = ((0.6 * focusPeakConf) + (0.4 * _eng));
  if ((_fam < 0.0)) {
    _fam = 0.0;
  }
  if ((_fam > 1.0)) {
    _fam = 1.0;
  }
  return _fam;
}
function _famCountTerm(n, famSat) {
  if (((famSat === null) || (famSat === undefined))) {
    famSat = 8.0;
  }
  if ((n <= 0.0)) {
    return 0.0;
  }
  let _conf = (n / (n + _CONF_HALF_C()));
  let _t = ((0.6 * _conf) + (0.4 * Math.min(1.0, (n / famSat))));
  if ((_t < 0.0)) {
    _t = 0.0;
  }
  if ((_t > 1.0)) {
    _t = 1.0;
  }
  return _t;
}
function _computeFamiliarityBlend(focusPeakConf, nEff, famSat, duration, affinity) {
  if (!_jac.builtin.bool(duration)) {
    duration = {};
  }
  if (!_jac.builtin.bool(affinity)) {
    affinity = {};
  }
  let _nDur = 0;
  let _durCats = _jac.poly.call(Object, "keys", duration);
  let _di = 0;
  while ((_di < _durCats.length)) {
    let _c = _durCats.at(_di);
    _di = (_di + 1);
    let _entry = duration[_c];
    let _nv = ((_entry && (_entry.n !== undefined)) ? _entry.n : 0);
    if (!_nv) {
      _nv = 0;
    }
    _nDur = (_nDur + _nv);
  }
  let _nAff = 0;
  let _nDay = 0;
  let _affCats = _jac.poly.call(Object, "keys", affinity);
  let _ai = 0;
  while ((_ai < _affCats.length)) {
    let _c2 = _affCats.at(_ai);
    _ai = (_ai + 1);
    let _aEntry = affinity[_c2];
    let _nAffV = ((_aEntry && (_aEntry.n !== undefined)) ? _aEntry.n : 0);
    if (!_nAffV) {
      _nAffV = 0;
    }
    _nAff = (_nAff + _nAffV);
    let _nDayV = ((_aEntry && (_aEntry.day_n !== undefined)) ? _aEntry.day_n : 0);
    if (!_nDayV) {
      _nDayV = 0;
    }
    _nDay = (_nDay + _nDayV);
  }
  let _dims = [];
  if (((nEff > 0.0) || (focusPeakConf > 0.0))) {
    _dims.push({"name": "focus_peak", "term": _computeFamiliarity(focusPeakConf, nEff, famSat), "n": nEff});
  }
  if ((_nDur > 0)) {
    _dims.push({"name": "duration", "term": _famCountTerm(parseFloat(_nDur), famSat), "n": _nDur});
  }
  if ((_nAff > 0)) {
    _dims.push({"name": "cat_time_affinity", "term": _famCountTerm(parseFloat(_nAff), famSat), "n": _nAff});
  }
  if ((_nDay > 0)) {
    _dims.push({"name": "day_balance", "term": _famCountTerm(parseFloat(_nDay), famSat), "n": _nDay});
  }
  if ((!_jac.builtin.bool(_dims) || (_dims.length === 0))) {
    return {"value": 0.0, "dimensions": []};
  }
  let _total = 0.0;
  let _dii = 0;
  while ((_dii < _dims.length)) {
    _total = (_total + _dims.at(_dii).term);
    _dii = (_dii + 1);
  }
  let _v = (_total / _dims.length);
  if ((_v < 0.0)) {
    _v = 0.0;
  }
  if ((_v > 1.0)) {
    _v = 1.0;
  }
  return {"value": _v, "dimensions": _dims};
}
function _durationCalibTarget(answer) {
  if (_jac.poly.contains(_DURATION_CALIB_MULT_C(), answer)) {
    return _jac.poly.getitem(_DURATION_CALIB_MULT_C(), answer);
  }
  return null;
}
function rollupDurationCalib(events, nowMs, halfLifeDays, minN) {
  if (!_jac.builtin.bool(events)) {
    events = [];
  }
  if (((halfLifeDays === null) || (halfLifeDays === undefined))) {
    halfLifeDays = 21.0;
  }
  if (((minN === null) || (minN === undefined))) {
    minN = 3.0;
  }
  let _acc = {};
  let _i = 0;
  while ((_i < events.length)) {
    let _e = events.at(_i);
    _i = (_i + 1);
    if (((_e.event_type || "") !== "feedback_answer")) {
      continue;
    }
    let _payload = (_e.payload ? _e.payload : {});
    if (((_payload.question_type || "") !== "duration_calib")) {
      continue;
    }
    let _answer = (_payload.answer || "");
    if ((_answer === "_dismissed")) {
      continue;
    }
    let _target = _durationCalibTarget(_answer);
    if (((_target === null) || (_target === undefined))) {
      continue;
    }
    let _cat = (_payload.category || "");
    if (!_cat) {
      _cat = (_e.category || "");
    }
    if (!_cat) {
      _cat = "other";
    }
    let _occMs2 = _parseIsoMs(_e.occurred_at);
    let _age2 = _ageDays(nowMs, _occMs2);
    let _decay2 = _decayFactor(_age2, halfLifeDays);
    let _base2 = parseFloat((((_e.signal_weight !== undefined) && (_e.signal_weight !== null)) ? _e.signal_weight : 1));
    if (!_base2) {
      _base2 = 1.0;
    }
    let _w2 = (_base2 * _decay2);
    if (!(_cat in _acc)) {
      _acc[_cat] = {"wsum": 0.0, "wmsum": 0.0, "n": 0};
    }
    _acc[_cat].wsum = (_acc[_cat].wsum + _w2);
    _acc[_cat].wmsum = (_acc[_cat].wmsum + (_w2 * _target));
    _acc[_cat].n = (_acc[_cat].n + 1);
  }
  let _out = {};
  let _cats = _jac.poly.call(Object, "keys", _acc);
  let _ci = 0;
  while ((_ci < _cats.length)) {
    let _cat3 = _cats.at(_ci);
    _ci = (_ci + 1);
    let _n3 = _acc[_cat3].n;
    let _gated3 = (_n3 >= Math.trunc(minN));
    let _raw3 = 100.0;
    if ((_acc[_cat3].wsum > 0.0)) {
      _raw3 = (_acc[_cat3].wmsum / _acc[_cat3].wsum);
    }
    let _mult3 = 100.0;
    if (_gated3) {
      _mult3 = _raw3;
      if ((_mult3 < _DUR_CLAMP_LO_C())) {
        _mult3 = _DUR_CLAMP_LO_C();
      }
      if ((_mult3 > _DUR_CLAMP_HI_C())) {
        _mult3 = _DUR_CLAMP_HI_C();
      }
    }
    _out[_cat3] = {"mult": _mult3, "n": _n3, "gated": _gated3, "raw": _raw3};
  }
  return _out;
}
function _taskAffinityIndex(events) {
  if (!_jac.builtin.bool(events)) {
    events = [];
  }
  let _idx = {};
  let _i = 0;
  while ((_i < events.length)) {
    let _e = events.at(_i);
    _i = (_i + 1);
    if (((_e.event_type || "") !== "feedback_answer")) {
      continue;
    }
    let _payload = (_e.payload ? _e.payload : {});
    if (((_payload.question_type || "") !== "move_attribution")) {
      continue;
    }
    if (((_payload.answer || "") !== "task_affinity")) {
      continue;
    }
    let _key = ((String(_payload.target_entity_id) + "|") + String(_payload.target_slot));
    if ((_key in _idx)) {
      continue;
    }
    _idx[_key] = true;
  }
  return _idx;
}
function _affEventSlot(event) {
  let _etype = (event.event_type || "");
  let _payload = (event.payload ? event.payload : {});
  let _slot = event.slot_minutes;
  if (((_slot !== null) && (_slot !== undefined))) {
    return _slot;
  }
  if ((_etype === "placement_moved")) {
    return ((_payload.to_slot !== undefined) ? _payload.to_slot : null);
  }
  if ((_etype === "placement_accepted")) {
    return ((_payload.slot !== undefined) ? _payload.slot : null);
  }
  return null;
}
function rollupCatAffinity(events, nowMs, halfLifeDays, minN, sepMargin) {
  if (!_jac.builtin.bool(events)) {
    events = [];
  }
  if (((halfLifeDays === null) || (halfLifeDays === undefined))) {
    halfLifeDays = 21.0;
  }
  if (((minN === null) || (minN === undefined))) {
    minN = 3;
  }
  if (((sepMargin === null) || (sepMargin === undefined))) {
    sepMargin = 0.15;
  }
  let _affIdx = _taskAffinityIndex(events);
  let _attrIdx = _attributionIndex(events);
  let _acc = {};
  let _i = 0;
  while ((_i < events.length)) {
    let _e = events.at(_i);
    _i = (_i + 1);
    let _etype = (_e.event_type || "");
    if (((_etype !== "placement_moved") && (_etype !== "placement_accepted"))) {
      continue;
    }
    let _sl = _affEventSlot(_e);
    if (((_sl === null) || (_sl === undefined))) {
      continue;
    }
    if ((_etype === "placement_moved")) {
      let _akey = ((String(_e.entity_id) + "|") + String(_sl));
      if (((_akey in _attrIdx) && _MOVE_DISAVOWALS_C().includes(_attrIdx[_akey].answer))) {
        continue;
      }
    }
    let _wgts = _softBucketWeights(_sl);
    if ((!_jac.builtin.bool(_wgts) || (_jac.poly.call(Object, "keys", _wgts).length === 0))) {
      continue;
    }
    let _cat = (_e.category || "");
    if (!_cat) {
      let _p = (_e.payload ? _e.payload : {});
      _cat = (_p.category || "");
    }
    if (!_cat) {
      _cat = "other";
    }
    let _occMs3 = _parseIsoMs(_e.occurred_at);
    let _age3 = _ageDays(nowMs, _occMs3);
    let _decay3 = _decayFactor(_age3, halfLifeDays);
    let _base3 = parseFloat((((_e.signal_weight !== undefined) && (_e.signal_weight !== null)) ? _e.signal_weight : 1));
    if (!_base3) {
      _base3 = 1.0;
    }
    let _w3 = (_base3 * _decay3);
    if ((_etype === "placement_accepted")) {
      _w3 = (_w3 * _AFF_ACCEPT_WEAK_C());
    }
    if ((_etype === "placement_moved")) {
      let _mkey = ((String(_e.entity_id) + "|") + String(_affEventSlot(_e)));
      if ((_mkey in _affIdx)) {
        _w3 = (_w3 * _AFF_TASK_AFFINITY_MULT_C());
      }
    }
    if (!(_cat in _acc)) {
      _acc[_cat] = {"buckets": {}, "n": 0};
    }
    let _bkeys = _jac.poly.call(Object, "keys", _wgts);
    let _bki = 0;
    while ((_bki < _bkeys.length)) {
      let _bk = _bkeys.at(_bki);
      _bki = (_bki + 1);
      let _prev3 = (_jac.poly.contains(_acc[_cat].buckets, _bk) ? _jac.poly.getitem(_acc[_cat].buckets, _bk) : 0.0);
      _acc[_cat].buckets[_bk] = (_prev3 + (_w3 * _wgts[_bk]));
    }
    _acc[_cat].n = (_acc[_cat].n + 1);
  }
  let _out = {};
  let _cats4 = _jac.poly.call(Object, "keys", _acc);
  let _c4i = 0;
  while ((_c4i < _cats4.length)) {
    let _cat4 = _cats4.at(_c4i);
    _c4i = (_c4i + 1);
    let _buckets4 = _acc[_cat4].buckets;
    let _n4 = _acc[_cat4].n;
    let _total4 = 0.0;
    let _bk4i = 0;
    let _bk4s = _jac.poly.call(Object, "keys", _buckets4);
    while ((_bk4i < _bk4s.length)) {
      _total4 = (_total4 + _jac.poly.getitem(_buckets4, _bk4s.at(_bk4i)));
      _bk4i = (_bk4i + 1);
    }
    let _dist4 = {};
    let _fp4i = 0;
    while ((_fp4i < _FP_ORDER_C().length)) {
      let _fb = _jac.poly.getitem(_FP_ORDER_C(), _fp4i);
      _fp4i = (_fp4i + 1);
      let _fbv = (_jac.poly.contains(_buckets4, _fb) ? _jac.poly.getitem(_buckets4, _fb) : 0.0);
      _dist4[_fb] = ((_total4 > 0.0) ? (_fbv / _total4) : 0.0);
    }
    let _argmax4 = _FP_ORDER_C()[0];
    let _best4 = _dist4[_argmax4];
    let _fp4j = 0;
    while ((_fp4j < _FP_ORDER_C().length)) {
      let _fb2 = _jac.poly.getitem(_FP_ORDER_C(), _fp4j);
      _fp4j = (_fp4j + 1);
      if ((_dist4[_fb2] > _best4)) {
        _best4 = _dist4[_fb2];
        _argmax4 = _fb2;
      }
    }
    let _runner4 = 0.0;
    let _fp4k = 0;
    while ((_fp4k < _FP_ORDER_C().length)) {
      let _fb3 = _jac.poly.getitem(_FP_ORDER_C(), _fp4k);
      _fp4k = (_fp4k + 1);
      if ((_fb3 === _argmax4)) {
        continue;
      }
      if ((_dist4[_fb3] > _runner4)) {
        _runner4 = _dist4[_fb3];
      }
    }
    let _sep4 = (_best4 - _runner4);
    let _gated4 = (((_n4 >= minN) && (_sep4 >= sepMargin)) && (_total4 > 0.0));
    let _preferred4 = (_gated4 ? _argmax4 : null);
    _out[_cat4] = {"dist": _dist4, "preferred": _preferred4, "n": _n4, "gated": _gated4, "sep": _sep4};
  }
  return _out;
}
function _rejectDayIndex(events) {
  if (!_jac.builtin.bool(events)) {
    events = [];
  }
  let _idx = {};
  let _i = 0;
  while ((_i < events.length)) {
    let _e = events.at(_i);
    _i = (_i + 1);
    if (((_e.event_type || "") !== "feedback_answer")) {
      continue;
    }
    let _payload = (_e.payload ? _e.payload : {});
    if (((_payload.question_type || "") !== "reject_attribution")) {
      continue;
    }
    let _answer = (_payload.answer || "");
    if ((_answer === "_dismissed")) {
      continue;
    }
    let _key = ((String(_payload.target_entity_id) + "|") + String(_payload.target_slot));
    if ((_key in _idx)) {
      continue;
    }
    _idx[_key] = _answer;
  }
  return _idx;
}
function rollupDayBalance(events, nowMs, halfLifeDays, minN) {
  if (!_jac.builtin.bool(events)) {
    events = [];
  }
  if (((halfLifeDays === null) || (halfLifeDays === undefined))) {
    halfLifeDays = 21.0;
  }
  if (((minN === null) || (minN === undefined))) {
    minN = 3;
  }
  let _rejIdx = _rejectDayIndex(events);
  let _attrIdx5 = _attributionIndex(events);
  let _acc5 = {};
  let _i = 0;
  while ((_i < events.length)) {
    let _e = events.at(_i);
    _i = (_i + 1);
    let _etype5 = (_e.event_type || "");
    let _payload5 = (_e.payload ? _e.payload : {});
    let _cat5 = (_e.category || "");
    if (!_cat5) {
      _cat5 = (_payload5.category || "");
    }
    if (!_cat5) {
      _cat5 = "other";
    }
    let _occMs5 = _parseIsoMs(_e.occurred_at);
    let _age5 = _ageDays(nowMs, _occMs5);
    let _decay5 = _decayFactor(_age5, halfLifeDays);
    let _votes = [];
    if ((_etype5 === "placement_rejected")) {
      let _rday = ((_e.day_of_week !== undefined) ? _e.day_of_week : null);
      if ((_rday === null)) {
        _rday = ((_payload5.rejected_day !== undefined) ? _payload5.rejected_day : null);
      }
      let _rslot5 = ((_e.slot_minutes !== undefined) ? _e.slot_minutes : null);
      if ((_rslot5 === null)) {
        _rslot5 = ((_payload5.rejected_slot !== undefined) ? _payload5.rejected_slot : null);
      }
      let _rkey5 = ((String(_e.entity_id) + "|") + String(_rslot5));
      if (((_rday !== null) && (((_rkey5 in _rejIdx) ? _rejIdx[_rkey5] : "") === "wrong_day"))) {
        _votes.push([Math.trunc(_rday), _DAY_REJECT_WRONG_C()]);
      }
    } else if ((_etype5 === "placement_moved")) {
      let _dkey5 = ((String(_e.entity_id) + "|") + String(_affEventSlot(_e)));
      if (((_dkey5 in _attrIdx5) && _MOVE_DISAVOWALS_C().includes(_attrIdx5[_dkey5].answer))) {
        continue;
      }
      let _fromDay5 = ((_payload5.from_day !== undefined) ? _payload5.from_day : null);
      let _toDay5 = ((_payload5.to_day !== undefined) ? _payload5.to_day : null);
      if ((_toDay5 === null)) {
        _toDay5 = ((_e.day_of_week !== undefined) ? _e.day_of_week : null);
      }
      if ((((_fromDay5 !== null) && (_toDay5 !== null)) && (Math.trunc(_fromDay5) !== Math.trunc(_toDay5)))) {
        _votes.push([Math.trunc(_toDay5), _DAY_MOVE_TO_C()]);
        _votes.push([Math.trunc(_fromDay5), _DAY_MOVE_FROM_C()]);
      }
    }
    if ((!_jac.builtin.bool(_votes) || (_votes.length === 0))) {
      continue;
    }
    if (!(_cat5 in _acc5)) {
      _acc5[_cat5] = {"days": {}, "n": 0};
    }
    let _vi = 0;
    while ((_vi < _votes.length)) {
      let _vote = _votes.at(_vi);
      _vi = (_vi + 1);
      let _dk5 = String(_vote[0]);
      let _wt5 = _vote[1];
      let _prev5 = (_jac.poly.contains(_acc5[_cat5].days, _dk5) ? _jac.poly.getitem(_acc5[_cat5].days, _dk5) : 0.0);
      _acc5[_cat5].days[_dk5] = (_prev5 + (_wt5 * _decay5));
    }
    _acc5[_cat5].n = (_acc5[_cat5].n + 1);
  }
  let _out5 = {};
  let _cats5 = _jac.poly.call(Object, "keys", _acc5);
  let _c5i = 0;
  while ((_c5i < _cats5.length)) {
    let _cat5b = _cats5.at(_c5i);
    _c5i = (_c5i + 1);
    let _days5 = _acc5[_cat5b].days;
    let _n5 = _acc5[_cat5b].n;
    let _peak5 = 0.0;
    let _dks5 = _jac.poly.call(Object, "keys", _days5);
    let _dki5 = 0;
    while ((_dki5 < _dks5.length)) {
      let _dk5b = _dks5.at(_dki5);
      _dki5 = (_dki5 + 1);
      let _av5 = ((_jac.poly.getitem(_days5, _dk5b) >= 0.0) ? _jac.poly.getitem(_days5, _dk5b) : -_jac.poly.getitem(_days5, _dk5b));
      if ((_av5 > _peak5)) {
        _peak5 = _av5;
      }
    }
    let _norm5 = {};
    let _dki5b = 0;
    while ((_dki5b < _dks5.length)) {
      let _dk5c = _dks5.at(_dki5b);
      _dki5b = (_dki5b + 1);
      if ((_peak5 > 0.0)) {
        _norm5[_dk5c] = (_jac.poly.getitem(_days5, _dk5c) / _peak5);
      } else {
        _norm5[_dk5c] = 0.0;
      }
    }
    _out5[_cat5b] = {"days": _norm5, "n": _n5, "gated": (_n5 >= minN)};
  }
  return _out5;
}
function rollupCoreClient(events, quizFocusPeak, prevLearned, nowIso) {
  if (!_jac.builtin.bool(events)) {
    events = [];
  }
  if (((quizFocusPeak === null) || (quizFocusPeak === undefined))) {
    quizFocusPeak = "";
  }
  if (((prevLearned === null) || (prevLearned === undefined))) {
    prevLearned = "";
  }
  let _halfLifeDays = 35.0;
  let _minN = 5.0;
  let _priorStrength = 8.0;
  let _sepMargin = 0.15;
  let _buckets = {"morning": 0.0, "midday": 0.0, "afternoon": 0.0, "evening": 0.0};
  let _nEff = 0.0;
  if ((quizFocusPeak in _buckets)) {
    _buckets[quizFocusPeak] = (_buckets[quizFocusPeak] + _priorStrength);
  }
  let _nowMs = _parseIsoMs(nowIso);
  let _affirmWindow = 14.0;
  let _movedIds = _recentMovedIds(events, _nowMs, _affirmWindow);
  let _affirmCount = 0;
  let _ai4 = 0;
  while ((_ai4 < events.length)) {
    if (_isAffirmation(events.at(_ai4), _movedIds, _nowMs, _affirmWindow)) {
      _affirmCount = (_affirmCount + 1);
    }
    _ai4 = (_ai4 + 1);
  }
  let _halfLifeEff = (_halfLifeDays * (1.0 + Math.min(1.5, (0.25 * _affirmCount))));
  let _attrIdx = _attributionIndex(events);
  let _traces = [];
  let _i = 0;
  while ((_i < events.length)) {
    let _e = events.at(_i);
    _i = (_i + 1);
    let _t = _weighEvent(_e, _attrIdx, _nowMs, _halfLifeEff);
    if ((_t.excluded && _isAffirmation(_e, _movedIds, _nowMs, _affirmWindow))) {
      _t.excluded_reason = "confounded for discovery; counted as policy affirmation (slows decay)";
    }
    _traces.push(_t);
    if (_t.excluded) {
      continue;
    }
    let _dkeys = _jac.poly.call(Object, "keys", _t.delta);
    let _dki = 0;
    while ((_dki < _dkeys.length)) {
      let _dk = _dkeys.at(_dki);
      _dki = (_dki + 1);
      _buckets[_dk] = (_buckets[_dk] + _jac.poly.getitem(_t.delta, _dk));
    }
    _nEff = (_nEff + _t.n_contrib);
  }
  let _fpki = 0;
  while ((_fpki < _FP_ORDER_C().length)) {
    let _fb = _jac.poly.getitem(_FP_ORDER_C(), _fpki);
    _fpki = (_fpki + 1);
    if ((_buckets[_fb] < 0.0)) {
      _buckets[_fb] = 0.0;
    }
  }
  let _total = 0.0;
  let _fpki2 = 0;
  while ((_fpki2 < _FP_ORDER_C().length)) {
    _total = (_total + _buckets[_jac.poly.getitem(_FP_ORDER_C(), _fpki2)]);
    _fpki2 = (_fpki2 + 1);
  }
  let _dist = {};
  if ((_total > 0.0)) {
    let _fpki3 = 0;
    while ((_fpki3 < _FP_ORDER_C().length)) {
      let _fb3 = _jac.poly.getitem(_FP_ORDER_C(), _fpki3);
      _fpki3 = (_fpki3 + 1);
      _dist[_fb3] = (_buckets[_fb3] / _total);
    }
  } else {
    let _fpki3b = 0;
    while ((_fpki3b < _FP_ORDER_C().length)) {
      _dist[_jac.poly.getitem(_FP_ORDER_C(), _fpki3b)] = 0.25;
      _fpki3b = (_fpki3b + 1);
    }
  }
  let _argmax = _FP_ORDER_C()[0];
  let _best = _dist[_argmax];
  let _fpki4 = 0;
  while ((_fpki4 < _FP_ORDER_C().length)) {
    let _fb4 = _jac.poly.getitem(_FP_ORDER_C(), _fpki4);
    _fpki4 = (_fpki4 + 1);
    if ((_dist[_fb4] > _best)) {
      _best = _dist[_fb4];
      _argmax = _fb4;
    }
  }
  let _quizShare = ((quizFocusPeak in _dist) ? _dist[quizFocusPeak] : 0.0);
  let _sep = (_dist[_argmax] - _quizShare);
  let _mergedBands = null;
  let _runner = null;
  let _runnerShare = (-1.0);
  let _fpki5 = 0;
  while ((_fpki5 < _FP_ORDER_C().length)) {
    let _fb5 = _jac.poly.getitem(_FP_ORDER_C(), _fpki5);
    _fpki5 = (_fpki5 + 1);
    if ((_fb5 === _argmax)) {
      continue;
    }
    if ((_dist[_fb5] > _runnerShare)) {
      _runnerShare = _dist[_fb5];
      _runner = _fb5;
    }
  }
  let _adjacent = false;
  if ((_runner !== null)) {
    _adjacent = (Math.abs((_FP_ORDER_C().indexOf(_argmax) - _FP_ORDER_C().indexOf(_runner))) === 1);
  }
  let _quizInPair = ((quizFocusPeak === _argmax) || (quizFocusPeak === _runner));
  let _aboveFloor = (_runnerShare >= (_F2_MERGE_NOISE_FLOOR_C() * _dist[_argmax]));
  if ((((((_runner !== null) && (_total > 0.0)) && _adjacent) && !_quizInPair) && _aboveFloor)) {
    _sep = ((_dist[_argmax] + _runnerShare) - _quizShare);
    let _aI = _FP_ORDER_C().indexOf(_argmax);
    let _rI = _FP_ORDER_C().indexOf(_runner);
    if ((_aI < _rI)) {
      _mergedBands = [_argmax, _runner];
    } else {
      _mergedBands = [_runner, _argmax];
    }
  }
  let _gateThreshold = (prevLearned ? _GATE_CLOSE_N_C() : _GATE_OPEN_N_C());
  let _nOk = (_nEff >= _gateThreshold);
  let _sepOk = (_sep >= _sepMargin);
  let _gated = (_nOk && _sepOk);
  let _learned = null;
  if (_gated) {
    _learned = _clampOneStep(prevLearned, _argmax);
  }
  let _rawConf = (_sep * (_nEff / (_nEff + _CONF_HALF_C())));
  let _conf = 0.0;
  let _confFormula = "";
  if (_gated) {
    _conf = Math.max(0.0, Math.min(1.0, _rawConf));
    _confFormula = "sep x n_eff/(n_eff+conf_half)";
  } else {
    _conf = Math.max(0.0, Math.min(1.0, (_rawConf * 0.5)));
    _confFormula = "sep x n_eff/(n_eff+conf_half) x 0.5 ungated";
  }
  let _gateInfo = {"n_ok": _nOk, "sep_ok": _sepOk, "open": _gated};
  if (_mergedBands) {
    _gateInfo["merged_bands"] = _mergedBands;
  }
  let _asks = _countFeedbackAnswers(events);
  let _appliedBucket = _consentAppliedBucket(events);
  let _consentApplied = ((_appliedBucket !== null) && (_appliedBucket === quizFocusPeak));
  let _duration = rollupDurationCalib(events, _nowMs, 21.0, 3.0);
  let _affinity = rollupCatAffinity(events, _nowMs, 21.0, 3, 0.15);
  let _dayBalance = rollupDayBalance(events, _nowMs, 21.0, 3);
  let _dbCats = _jac.poly.call(Object, "keys", _dayBalance);
  let _dbCi = 0;
  while ((_dbCi < _dbCats.length)) {
    let _dcat = _dbCats.at(_dbCi);
    _dbCi = (_dbCi + 1);
    if (!(_dcat in _affinity)) {
      _affinity[_dcat] = {"dist": {}, "preferred": null, "n": 0, "gated": false, "sep": 0.0};
    }
    _affinity[_dcat]["days"] = _dayBalance[_dcat]["days"];
    _affinity[_dcat]["day_n"] = _dayBalance[_dcat]["n"];
    _affinity[_dcat]["day_gated"] = _dayBalance[_dcat]["gated"];
  }
  let _confTerm = (0.6 * _conf);
  let _engTerm = (0.4 * Math.min(1.0, (_nEff / _FAM_SAT_C())));
  let _famBlend = _computeFamiliarityBlend(_conf, _nEff, _FAM_SAT_C(), _duration, _affinity);
  let _famValue = _famBlend["value"];
  let _prevLearnedOut = null;
  if (prevLearned) {
    _prevLearnedOut = prevLearned;
  }
  return {"events": _traces, "prior": {"bucket": quizFocusPeak, "strength": _priorStrength}, "accumulator": _buckets, "dist": _dist, "n_eff": _nEff, "min_n": _CONF_HALF_C(), "argmax": _argmax, "argmax_share": _dist[_argmax], "quiz_share": _quizShare, "separation": _sep, "sep_margin": _sepMargin, "gate_threshold": _gateThreshold, "gate_open_n": _GATE_OPEN_N_C(), "gate_close_n": _GATE_CLOSE_N_C(), "conf_half": _CONF_HALF_C(), "fam_sat": _FAM_SAT_C(), "gate": _gateInfo, "learned_focus_peak": _learned, "prev_learned": _prevLearnedOut, "confidence": {"value": _conf, "raw": _rawConf, "formula": _confFormula}, "familiarity": {"value": _famValue, "confidence_term": _confTerm, "engagement_term": _engTerm, "asks_answered": _asks, "dimensions": _famBlend["dimensions"], "formula": "mean over dims with data \u2014 focus: 0.6xcf + 0.4xmin(1, n_eff/8); duration/affinity/days: 0.6xn/(n+5) + 0.4xmin(1, n/8)"}, "affirmations": {"count": _affirmCount, "window_days": 14, "half_life_eff": _halfLifeEff}, "half_life_days": _halfLifeDays, "consent_applied": _consentApplied, "applied_bucket": _appliedBucket, "duration": _duration, "affinity": _affinity};
}
async function explainLearnedProfileViaTransport(uid, quizFocusPeak, accessToken) {
  if (!window.__skoochServerless) {
    let _p = window.__skoochJacCall("explain_learned_profile", {"user_id": uid, "quiz_focus_peak": quizFocusPeak, "access_token": accessToken});
    _p = _p.catch(e => {
      return null;
    });
    return await _p;
  }
  try {
    let _sb = get_supabase();
    let _ieTypes = ["feedback_answer", "preference_set", "placement_moved", "placement_rejected", "placement_accepted", "task_completed"];
    let _ieResp = await _sb.from("interaction_events").select("id,event_type,entity_id,category,occurred_at,slot_minutes,signal_weight,payload").eq("user_id", uid).in("event_type", _ieTypes).order("occurred_at", {"ascending": false}).limit(400);
    let _events = [];
    if (((_ieResp && !_ieResp.error) && _ieResp.data)) {
      _events = _ieResp.data;
    }
    let _prevLearned = "";
    let _lpResp = await _sb.from("learned_profile").select("learned_focus_peak").eq("user_id", uid).maybeSingle();
    if (((_lpResp && !_lpResp.error) && _lpResp.data)) {
      let _row = _lpResp.data;
      _prevLearned = ((_row.learned_focus_peak && (_row.learned_focus_peak !== undefined)) ? _row.learned_focus_peak : "");
    }
    let _nowIso = Reflect.construct(Date, []).toISOString();
    return rollupCoreClient(_events, quizFocusPeak, _prevLearned, _nowIso);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
      return null;
    } else {
      throw __jac_e;
    }
  }
}
export {_AFF_ACCEPT_WEAK_C, _AFF_GATE_N_C, _AFF_SEP_MARGIN_C, _AFF_TASK_AFFINITY_MULT_C, _ATTR_QTYPES_C, _CONF_HALF_C, _CONSENT_SOURCES_C, _DAY_GATE_N_C, _DAY_MOVE_FROM_C, _DAY_MOVE_TO_C, _DAY_REJECT_WRONG_C, _DURATION_CALIB_MULT_C, _DUR_CLAMP_HI_C, _DUR_CLAMP_LO_C, _DUR_GATE_N_C, _F2_MERGE_NOISE_FLOOR_C, _FAM_SAT_C, _FP_ORDER_C, _GATE_CLOSE_N_C, _GATE_OPEN_N_C, _MOVE_DISAVOWALS_C, _NONVOTING_QTYPES_C, _UNATTRIBUTED_MOVE_MOD_C, _affEventSlot, _ageDays, _attributionIndex, _attributionModifier, _bucketShift, _clampOneStep, _computeFamiliarity, _computeFamiliarityBlend, _consentAppliedBucket, _countFeedbackAnswers, _decayFactor, _durationCalibTarget, _eventAttrSlot, _eventFocusSignal, _eventLabel, _famCountTerm, _focusPeakBucket, _isAffirmation, _parseIsoMs, _pyRound, _recentMovedIds, _rejectDayIndex, _softBucketWeights, _taskAffinityIndex, _weighEvent, explainLearnedProfileViaTransport, rollupCatAffinity, rollupCoreClient, rollupDayBalance, rollupDurationCalib};
//# sourceMappingURL=learned_profile_client.js.map
